// Web Mercator ("slippy map") maths for the photo-location map: 256 px tiles, z/x/y.

export const TILE = 256;
const MAX_LAT = 85.05112878;

/** World pixel position of a point at a whole zoom level. */
export function worldPx(lat: number, lon: number, z: number): { x: number; y: number } {
  const size = TILE * 2 ** z;
  const s = Math.sin((Math.max(-MAX_LAT, Math.min(MAX_LAT, lat)) * Math.PI) / 180);
  return {
    x: ((lon + 180) / 360) * size,
    y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * size,
  };
}

export interface TileSpec {
  key: string;
  /** Tile address to fetch (x wrapped round the antimeridian). */
  z: number;
  x: number;
  y: number;
  /** Top-left corner relative to the map centre, in level-z pixels. */
  left: number;
  top: number;
}

/** Safety cap: a layer never asks for more than this many tiles (tile servers have fair-use limits). */
export const MAX_TILES = 48;

/**
 * Tiles at level z covering a width × height viewport centred on (lat, lon) when the
 * level is drawn at `scale` (1 = native, 2 = magnified to the next zoom level).
 */
export function tilesFor(lat: number, lon: number, z: number, width: number, height: number, scale: number): TileSpec[] {
  const c = worldPx(lat, lon, z);
  const n = 2 ** z;
  const halfW = width / (2 * scale);
  const halfH = height / (2 * scale);
  const x0 = Math.floor((c.x - halfW) / TILE);
  const x1 = Math.floor((c.x + halfW) / TILE);
  const y0 = Math.max(0, Math.floor((c.y - halfH) / TILE));
  const y1 = Math.min(n - 1, Math.floor((c.y + halfH) / TILE));
  // A layer drawn far below native size would need the whole area at full detail: refuse.
  if ((x1 - x0 + 1) * (y1 - y0 + 1) > MAX_TILES) return [];
  const out: TileSpec[] = [];
  for (let ty = y0; ty <= y1; ty++) {
    for (let tx = x0; tx <= x1; tx++) {
      out.push({ key: `${z}/${tx}/${ty}`, z, x: ((tx % n) + n) % n, y: ty, left: tx * TILE - c.x, top: ty * TILE - c.y });
    }
  }
  return out;
}

export function tileUrl(template: string, t: Pick<TileSpec, 'z' | 'x' | 'y'>): string {
  return template
    .replace('{s}', 'abc'[(t.x + t.y) % 3]!) // spread tiles over a./b./c. servers, stable per tile
    .replace('{z}', String(t.z))
    .replace('{x}', String(t.x))
    .replace('{y}', String(t.y));
}

/** Map zoom while a photo is on screen: fixed, or easing from `from` to `to` over the slide. */
export function zoomAt(elapsedSec: number, durationSec: number, from: number, to: number): number {
  if (durationSec <= 0) return to;
  const t = Math.min(1, Math.max(0, elapsedSec / durationSec));
  return from + (to - from) * t;
}

export const ZOOM_MIN = 2;
export const ZOOM_MAX = 18;

/** Plain-language name for a zoom level (settings labels). */
export function zoomLabel(z: number): string {
  if (z <= 3) return 'Continent';
  if (z <= 5) return 'Country';
  if (z <= 8) return 'Region';
  if (z <= 11) return 'City';
  if (z <= 13) return 'Town';
  if (z <= 15) return 'Streets';
  return 'Buildings';
}
